import { Component, ViewEncapsulation, ChangeDetectorRef, ViewChild, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import * as _ from 'lodash';
import { SelectEvent } from '@progress/kendo-angular-layout';
import { GlobalsService } from '@app/cp/shared/services/globals.service';
import { DQOIReferenceDataQuery } from '@app/cp/dqoi/state/dqoi-reference-data.query';
import { UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { BaseDQOIComponent } from '@app/cp/shared/base-components/base-dqoi.component';
import { DispatchesService } from '@app/cp/shared/services/dispatches.service';
import { DQOIService } from '@app/cp/shared/services/DQOI.service';
import { AdditionalCnstContractData } from '@app/cp/shared/models/AdditionalCnstContractData';
import { CnstContractPage } from '@app/cp/shared/models/CnstContractPage';
import { SearchTarget } from '@app/cp/shared/enums/SearchTarget';
import { SaveMode } from '@app/cp/shared/enums/SaveMode';
import { ConstructionJobsComponent } from '../jobs/construction-jobs.component';
import { CommonFunctions } from '@app/cp/shared/static/common-functions';
import { DQOIItem } from '@app/cp/shared/models/DQOIItem';
import { DQOIState } from '@app/cp/shared/models/DQOIState';
import { ConstructionService } from '@app/cp/shared/services/construction.service';
import { BaseMessageInfo } from '@app/cp/shared/models/BaseMessageInfo';
import { CnstMessageInfo } from '@app/cp/shared/models/CnstMessageInfo';
import { CnstContractBus } from '@app/cp/shared/models/CnstContractBus';
import { DateFunctions } from '@app/cp/shared/static/date-functions';
import { CnstContractSaveParam } from '@app/cp/shared/models/CnstContractSaveParam';
import { InvoiceMessageInfo } from '@app/cp/shared/models/InvoiceMessageInfo';
import { GItem } from '@app/cp/shared/models/GItem';
import { CommonValues } from '@app/cp/shared/static/CommonValues';
import { MessageInfoType } from '@app/cp/shared/enums/MessageInfoType';
import { CommonParam } from '@app/cp/shared/models/CommonParam';
import { WorkOrderNotesComponent } from '@app/cp/dispatches/notes/workorder-notes.component';
import { RefreshToken } from '@app/cp/shared/models/RefreshToken';
import { DispatchThirdPartyHistoryDialogComponent } from '../../dispatches/dispatch-third-party-history-dialog/dispatch-third-party-history-dialog.component';
import { InvoiceEstimateDialogComponent } from '../../invoices/invoice-estimate/invoice-estimate-dialog.component';
import { Email } from '../../shared/models/Email';
import { DQOIMessage } from '../../shared/models/DQOIMessage';
import { MessageType } from '../../shared/enums/MessageType';
import { ContractChangeOrdersComponent } from '../contracts/change-orders/contract-change-orders.component';
import { ContractMfgWarrantyJobsComponent } from '../contracts/mfg-warranty-jobs/contract-mfg-warranty-jobs.component';
import { ContractPostWarrantyJobsComponent } from '../contracts/post-warranty-jobs/contract-post-warranty-jobs.component';
import { ContractVendorCreditsComponent } from '../contracts/vendor-credits/contract-vendor-credits.component';
import { ProfitAnalysisLandingComponent } from './profit-analysis-landing/profit-analysis-landing.component';
import { aggregateBy } from '@progress/kendo-data-query';
import { FeatureFlagService } from "@app/cp/shared/services/feature-flag.service";
import { UseCPLoader, ICPLoader } from '@app/cp/shared/decorators/cploader/cploader-decorator';

@UseCPLoader()
@Component({
    selector: 'construction-landing',
    templateUrl: './construction-landing.component.html',
    styleUrls: ['./construction-landing.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})

export class ConstructionLandingComponent extends BaseDQOIComponent<CnstContractBus, ConstructionService, AdditionalCnstContractData, CnstContractPage> implements OnDestroy {
    appCode: string = "Contract";
    compLoader: ICPLoader = this as any;
    constructor(globals: GlobalsService, featureFlagService: FeatureFlagService, svc_dqoi: DQOIService, svc_data: ConstructionService, qry_rd: DQOIReferenceDataQuery, private cd: ChangeDetectorRef, private svc_disp: DispatchesService) {
        super(globals, featureFlagService, svc_dqoi, qry_rd);
        this.svc_data = svc_data;
        this.addtlDataClass = "AdditionalCnstContractData";
        this.pageData = new CnstContractPage(svc_data, null, svc_disp);
        this.searchTarget = SearchTarget.contract;
        this.saveMode = SaveMode.all;
        this.setupCheckForm();

        this.aggregates = [
            { field: 'total', aggregate: 'sum' },
            { field: 'payments', aggregate: 'sum' }
        ];
    }

    public aggregateResults;
    public aggregates: any[] = [];

    @ViewChild("profitAnalysisLanding") profitAnalysisLanding: ProfitAnalysisLandingComponent;

    searchTargetEnum: any = SearchTarget;

    @ViewChild("Notes") notesComp: WorkOrderNotesComponent;
    @ViewChild("jobs") jobsComp: ConstructionJobsComponent;
    @ViewChild("changeOrders") changeOrdersComp: ContractChangeOrdersComponent;
    @ViewChild("warrantyJobs") warrantyJobsComp: ContractMfgWarrantyJobsComponent;
    @ViewChild("postWarrantyJobs") postWarrantyJobsComp: ContractPostWarrantyJobsComponent;
    @ViewChild("vendorCredits") vendorCreditsComp: ContractVendorCreditsComponent;
    @ViewChild('dispatchThirdPartyHistoryDialog') public dispatchThirdPartyHistoryDialog: DispatchThirdPartyHistoryDialogComponent;
    @ViewChild('invoiceEstimateDialog') public invoiceEstimateDialog: InvoiceEstimateDialogComponent;

    public editForm: UntypedFormGroup = new UntypedFormGroup({
        'dispatchId': new UntypedFormControl(),
        'dispatchNumber': new UntypedFormControl(),
        'companyId': new UntypedFormControl(),
        'customerId': new UntypedFormControl(null, Validators.required),
        'locationId': new UntypedFormControl(null, Validators.required),
        'dispatchStatusId': new UntypedFormControl(null, Validators.required),
        'priorityId': new UntypedFormControl(),
        'notToExceed': new UntypedFormControl(),
        'ponumber': new UntypedFormControl('', Validators.required),
        'wonumber': new UntypedFormControl(),
        'quoteNumber': new UntypedFormControl(),
        'invoiceNumber': new UntypedFormControl(),
        'foremanId': new UntypedFormControl(),
        'jobClassificationId': new UntypedFormControl(),
        'dispatchClassificationId': new UntypedFormControl(),
        'dispatchRepairCatId': new UntypedFormControl(),
        'receivedDateTime': new UntypedFormControl(null, Validators.required),
        'receivedById': new UntypedFormControl(null, Validators.required),
        'estimatedBeginDate': new UntypedFormControl(null, Validators.required),
        'estimatedEndDate': new UntypedFormControl(null, Validators.required),
        'estimatedCost': new UntypedFormControl(),
        'isConstruction': new UntypedFormControl(false),
        'isProgressBill': new UntypedFormControl(false),
        'certPayrollRequired': new UntypedFormControl(false),
        'contractPrice': new UntypedFormControl(null as number),
        'profit1': new UntypedFormControl(null as number),
        'profit2': new UntypedFormControl(null as number),
        'dispatchReason': new UntypedFormControl(),
        'accountClassId': new UntypedFormControl(null, Validators.required),
        'acctMgr': new UntypedFormControl(),
        'whoCalledName': new UntypedFormControl(),
        'refTrackNbr': new UntypedFormControl(),
        'drawDateInfo': new UntypedFormControl(),
        'jobWarrantyEndDate': new UntypedFormControl(),
        'contractDivisionId': new UntypedFormControl(null, Validators.required),
        'contractAdminId': new UntypedFormControl(null, Validators.required),
        'contractProjAdminId': new UntypedFormControl(null, Validators.required),
        'contractJrProjAdminId': new UntypedFormControl(null),
        'finalBillingDt': new UntypedFormControl(),

        'dispatchActionCodesId': new UntypedFormControl(),
        'dispatchCauseCodesId': new UntypedFormControl(),
        'dispatchFailureCodesId': new UntypedFormControl(),
        'dispatchProblemCodeId': new UntypedFormControl(),
        'refreshDate': new UntypedFormControl(),

        'insertUserId': new UntypedFormControl(),
        'insertDt': new UntypedFormControl(),
        'updateUserid': new UntypedFormControl(),
        'updateDt': new UntypedFormControl(),
    });
    setForm() {
        this.editForm.patchValue({
            dispatchId: this.data.dispatchId,
            dispatchNumber: this.data.dispatchNumber,
            companyId: this.data.companyId,
            customerId: this.data.customerId,
            locationId: this.data.locationId,
            dispatchStatusId: this.data.dispatchStatusId,
            priorityId: this.data.priorityId,
            notToExceed: this.data.notToExceed,
            ponumber: this.data.ponumber,
            wonumber: this.data.wonumber,
            foremanId: this.data.foremanId,
            jobClassificationId: this.data.jobClassificationId,
            dispatchClassificationId: null,
            dispatchRepairCatId: null,
            receivedDateTime: this.data.receivedDateTime,
            receivedById: this.data.receivedById,
            estimatedBeginDate: this.data.estimatedBeginDate,
            estimatedEndDate: this.data.estimatedEndDate,
            estimatedCost: this.data.estimatedCost,
            isConstruction: this.data.isConstruction,
            isProgressBill: this.data.isProgressBill,
            certPayrollRequired: this.data.certPayrollRequired,
            contractPrice: this.data.contractPrice,
            dispatchReason: this.data.dispatchReason,
            accountClassId: this.data.accountClassId,
            whoCalledName: this.data.whoCalledName,
            refTrackNbr: this.data.refTrackNbr,
            drawDateInfo: this.data.drawDateInfo,
            jobWarrantyEndDate: this.data.jobWarrantyEndDate,
            contractDivisionId: this.data.contractDivisionId,
            contractAdminId: this.data.contractAdminId,
            contractProjAdminId: this.data.contractProjAdminId,
            contractJrProjAdminId: this.data.contractJrProjAdminId,
            finalBillingDt: this.data.finalBillingDt,

            dispatchActionCodesId: this.data.dispatchActionCodesId,
            dispatchCauseCodesId: this.data.dispatchCauseCodesId,
            dispatchFailureCodesId: this.data.dispatchFailureCodesId,
            dispatchProblemCodeId: this.data.dispatchProblemCodeId,
            refreshDate: this.data.refreshDate,

            inserDt: this.data.insertDt,
            insertUserId: this.data.insertUserId,
            updateDt: this.data.updateDt,
            updateUserId: this.data.updateUserid
        });
    }
    get canDelete(): boolean {
        return false;
    }

    get receivedDate(): any {
        return this.editForm.value.receivedDateTime;
    }

    setFormAddtl() {
        this.editForm.patchValue({
            //quoteNumber: this.addtlData.quoteNumber,
            //invoiceNumber: this.addtlData.invoiceNumber,
            //profit1: this.addtlData.profit1,
            //profit2: this.addtlData.profit2,
            //acctMgr: this.addtlData.acctMgr
        });

    }

    createNew(busObj: CnstContractBus) {
        let newBusObj = new CnstContractBus(busObj);

        return newBusObj;
    }
    subs_createDraw: Subscription;
    subs_statusChangeCheck: Subscription;
    subs_checkClosing: Subscription;
    subs_closeoutDateChanged: Subscription;
    override ngOnDestroy() {
        super.ngOnDestroy();
        CommonFunctions.unsubscribe(this.subs_createDraw);
        CommonFunctions.unsubscribe(this.subs_statusChangeCheck);
        CommonFunctions.unsubscribe(this.subs_checkClosing);
        CommonFunctions.unsubscribe(this.subs_closeoutDateChanged);
    }
    refresh() {
        if (!this.refreshToken || this.refreshToken == 0) {
            this.refreshToken = 1;
        }
        //else {
        //    this.refreshToken++;
        //}
        this.getData();
        this.refreshData(null);
        this.refreshToken = 0;
    }
    refreshDQOI(token: RefreshToken = null) {
        if (!this.isDirty) {
            this.refresh();
        }
    }
    public openNewTab(linkObj: any) {
        window.open(linkObj.url, "_blank");
    }

    getData() {
        this.mainLoadingOn(true, this.compLoader);
        super.getData();
        CommonFunctions.unsubscribe(this.subs_data);
        this.profitAnalysisLanding.loadData();
        if (!this.id) {
            this.mainLoadingOn(false, this.compLoader);
            return;
        }
        this.subs_data = this.svc_data.getContractInfoOnly(this.id).subscribe((info: BaseMessageInfo) => {
            let dataObj: CnstContractBus = new CnstContractBus(info.primaryReturn);
            this.setData(dataObj);
            this.tempUpdateDt = dataObj.updateDt;
            this.mainLoadingOff(true, this.compLoader);
        });
    }
    afterDataSet() {
        this.pageData.getContractInfoOther(this.id);
        this.pageData.getNotes(this.id);
        this.pageData.getOrdersByContract(this.id)
        this.pageData.getInvoices(this.id);
        this.pageData.getContactJobCosts(this.id);
        this.pageData.getDispatchCostAdjustments(this.id);

        //this.svc_data.getContractInfoOther(this.id).subscribe((info: BaseMessageInfo) => {
        //    let dataObj: CnstContractBus = new CnstContractBus(info.primaryReturn);
        //});

    }
    getAddtlData() {
        this.pageData.getNotes(this.id);
    }
    setData(data: CnstContractBus) {
        this.customerChanged(data.customerId);
        this.locationChanged(data.locationId);
        super.setData(data);
        this.readOnly = false;
        this.processGridData();
    }
    protected resetData(dqoiItem: DQOIItem) {
        super.resetData(dqoiItem);
        if (dqoiItem.hasContractId) {
            this.id = dqoiItem.dispatchId;
            this.getData();
        }
    }
    setDQOIState() {
        let data: DQOIState = this.svc_dqoi.getDQOIState();
        data.dispatchId = this.data.dispatchId;
        if (!this.settingFromDQOIState) {
            data.customerId = this.data.customerId;
            data.invoiceId = this.data.invoiceId;
            data.quoteId = this.data.quoteId;
        }
        this.svc_dqoi.nextDQOIState(data);
        this.settingFromDQOIState = false;;
    }
    resetDQOIItem(itemData: DQOIItem) {

        let itemBus: DQOIItem = new DQOIItem(itemData);
        this.svc_dqoi.setDQOIItem(itemBus);
    }
    protected saveSuccess(message: string = null) {
        super.saveSuccess(message);
        let dMmessage: DQOIMessage = new DQOIMessage();

        dMmessage.messageData = this.data.dispatchId;
        dMmessage.messageType = MessageType.childSaved;
        this.dqoiMessage.emit(dMmessage);

    }

    protected dqoiMessageNote(e:any) {
        if (e.messageType === MessageType.childSaved) {
            let dMmessage: DQOIMessage = new DQOIMessage();

            dMmessage.messageData = this.data.dispatchId;
            dMmessage.messageType = MessageType.childSaved;
            this.dqoiMessage.emit(dMmessage);
        }

    }

    //setDQOIItem(busObj: CnstContractBus) {
    //    let item: DQOIItem = this.svc_dqoi.getDQOIItem(this.dqoiIndex);
    //    let itemBus: DQOIItem = new DQOIItem(item);
    //    itemBus.dispatchId = busObj.dispatchId;
    //    itemBus.dispatchNumber = busObj.dispatchNumber;
    //    itemBus.customerId = busObj.customerId;
    //    itemBus.locationId = busObj.locationId;

    //    this.svc_dqoi.setDQOIItem(itemBus);
    //}
    test() {
        this.setFormFlags();
        let cfv = this.customFormValidation();
        let asv = this.appSaveValidation();
        console.log("test: ", this.canSaveValue, this.canEditValue, this.canSave, this.canEdit, this.formValid, cfv,asv, this);
//        let item: DQOIItem = this.svc_dqoi.getDQOIItem(this.dqoiIndex);
//        let itemBus: DQOIItem = new DQOIItem(item);

    }
    onTabSelect(e: SelectEvent) {
        if (e.title === 'Status History' && !this.pageData.hasStatusHist) {
            this.pageData.getStatusHistory(this.data.dispatchId);
        }
        else if (e.title === 'Notes' && !this.pageData.hasNotes) {
            this.pageData.getNotes(this.data.dispatchId);
        }
        else if (e.title === 'Vendor Credits' && !this.pageData.hasVendorCredits) {
            this.pageData.getVendorCreditsByContract(this.data.dispatchId);
        }
        //else Status History
    }
    tempUpdateDt: Date;
    saveAll() {
        let now: Date = DateFunctions.getDateNow();
        let busObj: CnstContractSaveParam = new CnstContractSaveParam(this.data);

        busObj.setFromForm(this.editForm.value);

        //////busObj.billingGeographyInfo = this.billToGeoInfoComp.getData();
        //////busObj.shippingGeographyInfo = this.locGeoInfoComp.getData();

        busObj.setCommon(this.globals.userId, now, this.globals.companyId);

       busObj.setBeforeSave();

        //// busObj.copyData(this.addtlData, this.pageData);
        if (CommonFunctions.hasValue(this.notesComp)) {
            busObj.notes = _.cloneDeep(this.notesComp.getChanged());
            busObj.addDeletedItems(this.notesComp.getDeletedItems());
        }
        if (CommonFunctions.hasValue(this.changeOrdersComp)) {
            busObj.jobChangeOrders = _.cloneDeep(this.changeOrdersComp.getChanged());
        }
        console.log("dispatch-save-all-bs-b: ", busObj);
        this.mainLoadingOn(true, this.compLoader);
        CommonFunctions.unsubscribe(this.subs_save);
        this.subs_save = this.svc_data.contractSaveAll(busObj).subscribe((info:  CnstMessageInfo) => {
            this.mainLoadingOff(true, this.compLoader);
            console.log("dispatch-save-all-as: ", info);
            if (!info.saveError) {
                if (!busObj.hasId) {
                    info.dqoiItem.index = this.dqoiIndex;
                    this.resetDQOIItem(info.dqoiItem);
                }
                this.getDataFromId(info.contract.dispatchId);
                this.saveSuccess();
            }
            else {
                this.saveError(info.message);
            }
        });
    }
    delete() {
        if (this.data.isAdded) {
            this.clear();
        }
        else {
            CommonFunctions.unsubscribe(this.subs_delete);
            this.subs_delete = this.svc_disp.deleteDispatch(this.id).subscribe((info: BaseMessageInfo) => {
                if (!info.saveError) {
                    this.deleteSuccess();
                    this.clear();
                }
                else {
                    this.deleteError(info.message)
                }
            });
        }
    }
    createDraw() {
        this.compLoader.cpLoaderOn(true);
        CommonFunctions.unsubscribe(this.subs_createDraw);
        this.subs_createDraw = this.svc_data.createDraw(this.id).subscribe((info: InvoiceMessageInfo) => {
            //this.svc_dqoi.getDQOIItem(this.dqoiIndex);
            this.compLoader.cpLoaderOff();
            this.goTo(info, SearchTarget.invoice,"CRFROMDRAW");
        });
    }
    statusCBOChanged(item: GItem) {
        if (item.value === CommonValues.Dispatch_Billed) {
            CommonFunctions.unsubscribe(this.subs_statusChangeCheck);
            this.subs_statusChangeCheck = this.svc_data.checkContractStatusChange(this.id, item.value).subscribe((info: CnstMessageInfo) => {
                if (info.statusChangeOk === false) {
                    let title: string = "Status Change";
                    this.miShow("Change Status Warning: " + (info.saveError ? "Error" : "Warning"), info, info, title, null, null);
                    this.patchStatus(this.data.dispatchStatusId);
                }
            });
        }
    }
    patchStatus(statusId: any) {
        this.editForm.patchValue({
            dispatchStatusId: statusId
        });

    }
    get statusId(): string {
        return this.editForm.value.dispatchStatusId;
    }
    get finalBillingDt(): boolean {
        return this.editForm.value.finalBillingDt;
    }
    beforeCloseoutDateDirty: boolean = false;
    closeoutDateFocus() {
        this.beforeCloseoutDateDirty = this.isDirty;
    }
    closeoutDateBlur() {
        this.beforeCloseoutDateDirty = false;
    }
    checkClosing() {
        let param: CommonParam = new CommonParam();
        param.dcParams = new Object();
        param.dcParams["ContractId"] = this.id;
        param.dcParams["StatusId"] = CommonValues.Dispatch_Closed;
        param.dcParams["FinalBillingDt"] = DateFunctions.getDateNow();
        CommonFunctions.unsubscribe(this.subs_checkClosing);
        this.subs_checkClosing = this.svc_data.validateOpenJobs(param).subscribe((info: CnstMessageInfo) => {
            info = new CnstMessageInfo(info);
            if (info.hasNonConfirmMessages) {
                let message: string = info.dcValues.MessageDesc;
                let title: string = "Closeout Date Warning";// + (info.saveError ? "Error" : "Warning");
                this.miShow(message, info, info, title, null, null, MessageInfoType.warning);
            }
            else {
                let title: string = "Closeout Check";// + (info.saveError ? "Error" : "Warning");
                let message: string = "Closeout Ok";
                this.miShow(message, info, info, title, null, null, MessageInfoType.information);
            }
        });
    }
    viewHistory() {

        this.dispatchThirdPartyHistoryDialog.openHistory(this.id);

    }
    viewInvoiceEstimate() {

        this.invoiceEstimateDialog.show(this.id, SearchTarget.dispatch);

    }
    closeoutDateChanged(value: Date) {
        let param: CommonParam = new CommonParam();
        param.dcParams = new Object();
        param.dcParams["ContractId"] = this.id;
        param.dcParams["StatusId"] = this.statusId;
        param.dcParams["FinalBillingDt"] = this.finalBillingDt;
        CommonFunctions.unsubscribe(this.subs_closeoutDateChanged);
        this.subs_closeoutDateChanged = this.svc_data.validateOpenJobs(param).subscribe((info: CnstMessageInfo) => {
            info = new CnstMessageInfo(info);
            if (info.hasNonConfirmMessages) {
                let message: string = info.dcValues.MessageDesc;
                let title: string = "Closeout Date Warning";// + (info.saveError ? "Error" : "Warning");
                this.miShow(message, info, info, title, null, null, MessageInfoType.warning);
                this.patchCloseoutDate(null);
                if (!this.beforeCloseoutDateDirty) {
                    this.setPristine();
                }
            }
        });
    }
    protected patchCloseoutDate(value: Date) {
        this.editForm.patchValue({
            finalBillingDt: value
        });

    }
    get canEditCloseoutDate(): boolean {
        let canEdit: boolean = !this.readOnly && this.statusId === CommonValues.Dispatch_Closed;

        return canEdit;
    }
    get canEditDivision(): boolean {
        let canEdit: boolean = true;
        if (this.receivedDate) {
            let now = CommonFunctions.getDateNow();
            let created = CommonFunctions.getDate(this.receivedDate);
            canEdit = !this.readOnly && (((now.getTime() - created.getTime()) / (1000 * 3600 * 24)) < 1);

        } else {

        }
        //return this.receivedDate ? canEdit : true;
        return canEdit;

    }
    get readOnlyReceivedBy(): boolean {
        return this.readOnly || (this.data ? !this.data.isAdded : false);
    }
    get emailTitle(): string {
        return this.hasId && this.customerId === CommonValues.Customer_WalMart ? 'WalMart Email' : 'Email';
    }
    get canEmail(): boolean {
        return this.hasId ; //this.serviceMode !== '1';
    }
    email() {
        //CPM 02/23/22: Construction does not need a different email for Wal-Mart
        if (this.customerId === CommonValues.Customer_WalMart && false) {
            this.svc_disp.getWalMartEmail(this.id).subscribe((info: BaseMessageInfo) => {
                if (!info.saveError) {
                    let email: Email = new Email(info.primaryReturn);
                    window.location.assign("mailto: ?subject=" + email.subjectEncoded() + "&body=" + email.bodyEncoded());
                }
                else {
                    this.saveError("Error Trying to Construct Email: " + info.message);
                }
            });
        }
        else {
            this.svc_disp.getEmailDefaultsForDispatch(this.id).subscribe((info: BaseMessageInfo) => {
                if (!info.saveError) {
                    let email: Email = new Email(info.primaryReturn);
                    window.location.assign("mailto: ?subject=" + email.subjectEncoded() + "&body=" + email.bodyEncoded());
                }
                else {
                    this.saveError("Error Trying to Construct Email: " + info.message);
                }
            });
        }
    }
    setup(data: any, code: string = null) {
        console.log("const-setup: ", data, code);
        if (code === "CRFROMSVCJOB") {
            let busObj: CnstContractBus = this.createNew(data.data);
            busObj.dispatchId = null;
            busObj.dispatchNumber = "";
            busObj.relatedJobTypeId = CommonValues.RelatedJobType_Contract;
            busObj.dispatchStatusId = CommonValues.Dispatch_ReadyForDispatch;
            this.setData(busObj);
            console.log("const-setup-b: ", busObj);
        }

    }

    processGridData() {
        if (CommonFunctions.hasArrayElementValue(this.pageData.invoices)) {
            this.aggregateResults = aggregateBy(this.pageData.invoices, this.aggregates);
        }
    }
    public personFromUserId(id: string) {
        return this.referenceData.personsSel.find(x => x.userId === id);
    }
}
